import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft } from '@tabler/icons-react';
import PostCard from '../components/PostCard';
import Footer from '../components/Footer';
import { db, storage, functions } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import './PostDetailPage.css';

const DEFAULT_AVATAR = '/default-avatar.png';

const PostDetailPage = ({ user, onLoginRequired }) => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadedPostIdRef = useRef(null);
  const [templateLogos, setTemplateLogos] = useState({});

  const toDate = (value) => {
    if (!value) return new Date();
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? new Date() : new Date(parsed);
  };

  // Load template logos for PostCard display (same pattern as CreatePage)
  useEffect(() => {
    if (!user) return;
    const loadTemplateLogos = async () => {
      try {
        const getTemplatesFunc = httpsCallable(functions, 'getPromptTemplates');
        const result = await getTemplatesFunc();
        if (result.data?.templates) {
          const logoMap = {};
          result.data.templates.forEach(t => {
            if (t.logoUrl) {
              let baseName = t.name;
              if (baseName.includes(' 3D')) baseName = baseName.replace(' 3D', '').trim();
              if (baseName.endsWith('3D')) baseName = baseName.slice(0, -2).trim();
              if (!logoMap[baseName]) logoMap[baseName] = t.logoUrl;
            }
          });
          setTemplateLogos(logoMap);
        }
      } catch (err) {
        console.error('Error loading template logos:', err);
      }
    };
    loadTemplateLogos();
  }, [user]);

  useEffect(() => {
    console.log('[PostDetailPage] useEffect triggered - postId:', postId, 'user:', user?.uid);
    // Prevent duplicate loads of the same post
    if (loadedPostIdRef.current === postId) {
      console.log('[PostDetailPage] Post already loaded, skipping');
      return;
    }
    loadedPostIdRef.current = postId;
    loadPost();
    // Only reload when postId changes, not when user changes
  }, [postId]);

  const loadPost = async () => {
    console.log('[PostDetailPage] loadPost called for postId:', postId);
    setIsLoading(true);
    try {
      const docRef = doc(db, 'GenerationRequests', postId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        setPost(null);
        return;
      }

      const mapData = docSnap.data();
      const isPublic = mapData.isPublic !== false;
      const isOwner = user?.uid && mapData.userId === user.uid;

      if (!isPublic && !isOwner) {
        setPost(null);
        return;
      }

      let generatedUrl = null;
      let generated2dUrl = null;
      let originalUrl = null;
      let videoUrl = null;

      if (mapData.generatedImagePath) {
        try {
          generatedUrl = await getDownloadURL(ref(storage, mapData.generatedImagePath));
        } catch (downloadError) {
          console.warn('Failed to load generated image URL:', downloadError);
        }
      }

      // Watopia dual-map render-only generations carry a secondary 2D map
      if (mapData.generatedImagePath2d) {
        try {
          generated2dUrl = await getDownloadURL(ref(storage, mapData.generatedImagePath2d));
        } catch (downloadError) {
          console.warn('Failed to load secondary 2D image URL:', downloadError);
        }
      }

      if (mapData.originalImagePath) {
        try {
          originalUrl = await getDownloadURL(ref(storage, mapData.originalImagePath));
        } catch (downloadError) {
          console.warn('Failed to load original image URL:', downloadError);
        }
      }

      if (mapData.video?.path) {
        try {
          videoUrl = await getDownloadURL(ref(storage, mapData.video.path));
        } catch (downloadError) {
          console.warn('Failed to load transition video URL:', downloadError);
        }
      }

      const mediaItems = [
        generatedUrl ? { kind: 'image', uri: generatedUrl, role: 'generated' } : null,
        generated2dUrl ? { kind: 'image', uri: generated2dUrl, role: 'generated-2d' } : null,
        videoUrl ? { kind: 'video', uri: videoUrl, role: 'video', orientation: mapData.video?.orientation } : null,
        originalUrl ? { kind: 'image', uri: originalUrl, role: 'original' } : null,
      ].filter(Boolean);

      if (mediaItems.length === 0) {
        mediaItems.push({ kind: 'image', uri: '/demo-maps/itoshima-ride.png', role: 'generated' });
      }

      const resolvedLocation = typeof mapData.location === 'string'
        ? mapData.location
        : mapData.location?.formatted || mapData.location?.city || '';

      const username = mapData.userUsername || mapData.userDisplayName || '';
      const displayName = mapData.userDisplayName || username;
      const avatar = mapData.userAvatar || DEFAULT_AVATAR;

      const activityName = mapData.activityName || mapData.stravaStats?.activityName || 'Untitled Map';
      const activityTypeRaw = mapData.activityType || mapData.stravaStats?.activityType || 'ride';
      const stravaId = mapData.StravaId || mapData.stravaId || null;

      // Use initial badges from Firestore
      const badges = mapData.badges || mapData.badgeMetadata?.badges || [];

      const postPayload = {
        id: postId,
        userId: mapData.userId,
        user: {
          id: mapData.userId,
          username,
          displayName,
          avatar
        },
        images: mediaItems.filter((item) => item.kind === 'image').map((item) => item.uri),
        media: mediaItems,
        caption: activityName,
        activityType: typeof activityTypeRaw === 'string' ? activityTypeRaw.toLowerCase() : 'ride',
        stravaActivityId: stravaId,
        createdAt: toDate(mapData.createdAt),
        location: resolvedLocation,
        styleName: mapData.styleName || mapData.style || 'Custom',
        badges,
        stravaStats: mapData.stravaStats || null,
        isPublic,
        mapOrientation: mapData.mapOrientation || 'landscape',
      };

      setPost(postPayload);
      setIsLoading(false);

      // Hydrate additional data asynchronously in background
      hydratePostData(mapData, postPayload);
    } catch (error) {
      console.error('Error loading post:', error);
      setPost(null);
      setIsLoading(false);
    }
  };

  const hydratePostData = async (mapData, initialPost) => {
    console.log('[PostDetailPage] hydratePostData called - user:', user?.uid, 'postId:', postId);
    if (!user) return;

    // Hydrate user profile if needed
    if (mapData.userId) {
      const shouldFetchProfile = !mapData.userUsername || !mapData.userDisplayName || !mapData.userAvatar || initialPost.user.username === mapData.userId;
      console.log('[PostDetailPage] shouldFetchProfile:', shouldFetchProfile);
      if (shouldFetchProfile) {
        try {
          console.log('[PostDetailPage] Calling getUserProfilePublic for userId:', mapData.userId);
          const getUserProfile = httpsCallable(functions, 'getUserProfilePublic');
          const profileResponse = await getUserProfile({ userId: mapData.userId });
          const profileData = profileResponse?.data || null;
          if (profileData) {
            setPost(prev => prev ? {
              ...prev,
              user: {
                ...prev.user,
                username: profileData.username || prev.user.username,
                displayName: profileData.displayName || prev.user.displayName,
                avatar: profileData.photoURL || prev.user.avatar
              }
            } : prev);
          }
        } catch (profileError) {
          console.warn('Failed to load user profile info:', profileError);
        }
      }
    }

    // Hydrate badges
    try {
      const getUserBadgesFunc = httpsCallable(functions, 'getUserBadges');
      const badgesResult = await getUserBadgesFunc({ generationRequestId: postId });
      if (badgesResult?.data?.badges && Array.isArray(badgesResult.data.badges) && badgesResult.data.badges.length > 0) {
        setPost(prev => prev ? { ...prev, badges: badgesResult.data.badges } : prev);
      }
    } catch (err) {
      console.log(`[PostDetailPage] Error fetching badges:`, err.message);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  if (isLoading) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-loading">
          <div className="spinner"></div>
          <p>{t('postDetailPage.loadingPost', 'Loading post...')}</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-not-found">
          <h2>{t('postDetailPage.postNotFound', 'Post not found')}</h2>
          <p>{t('postDetailPage.postNotFoundDesc', "This post may have been deleted or doesn't exist.")}</p>
          <button onClick={() => navigate('/')}>{t('postDetailPage.goHome', 'Go Home')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      {/* Back Button */}
      <div className="post-detail-header">
        <button className="back-button" onClick={handleBack}>
          <IconArrowLeft size={20} />
          <span>{t('postDetailPage.back', 'Back')}</span>
        </button>
      </div>

      {/* Single Post */}
      <div className="post-detail-content">
        <div className={`post-detail-card-wrapper ${post.mapOrientation === 'portrait' ? 'portrait-map' : 'landscape-map'}`}>
          <PostCard
            post={post}
            user={user}
            onLoginRequired={onLoginRequired}
            templateLogos={templateLogos}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PostDetailPage;
